/*
Copyright (C) 2026  Integrated Tech9labs Pvt. Ltd.
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

/*
 * Migration evidence pack (the portal's server, server/api/EvidenceApi.ts):
 *
 *   GET /api/evidence/deployments/<id>.pdf | .json
 *   GET /api/evidence/transfers/<id>.pdf | .json
 *
 * The server reads everything with the caller's Swift session (X-Auth-Token, which ApiCaller
 * sends, or the "token" cookie, which a plain link sends), so RBAC applies. A plain
 * <a href={EvidenceSource.pdfUrl("deployment", id)} download> works too; download() below
 * shows errors instead of saving an error page.
 */

import { saveAs } from "file-saver";

import Api from "@src/utils/ApiCaller";

import type { Evidence } from "../../server/evidence/types";

export type { Evidence };

export type EvidenceKind = "deployment" | "transfer";

const SEGMENT: { [k in EvidenceKind]: string } = {
  deployment: "deployments",
  transfer: "transfers",
};

const fileNameFrom = (disposition: string | undefined, fallback: string) => {
  const m = /filename="?([^";]+)"?/i.exec(disposition || "");
  return m ? m[1] : fallback;
};

/** The message of a failed pack request ({"error": {"message"}} as JSON, possibly inside a Blob). */
export const errorText = async (err: any): Promise<string> => {
  const status = err?.status;
  let data = err?.data;
  try {
    if (data && typeof Blob !== "undefined" && data instanceof Blob) {
      data = await data.text();
    }
    if (typeof data === "string" && data.trim().startsWith("{")) {
      data = JSON.parse(data);
    }
  } catch (e) {
    // keep what we have
  }
  const message = data?.error?.message || (typeof data === "string" && data.trim() && !data.trim().startsWith("<") ? data.trim() : null);
  if (message) return String(message);
  if (status === 401) return "Your SwiftIQ session has expired: sign in again.";
  if (status === 403) return "You are not allowed to read this migration's records.";
  if (status === 404) return "SwiftIQ could not find this migration.";
  if (status) return `The evidence pack could not be built (HTTP ${status}).`;
  return "The evidence pack could not be built: no answer from the portal's server.";
};

class EvidenceSource {
  /** The pack's URL; `inline` shows it in the browser instead of downloading it. */
  pdfUrl(kind: EvidenceKind, id: string, inline = false): string {
    return `/api/evidence/${SEGMENT[kind]}/${encodeURIComponent(id)}.pdf${inline ? "?inline=1" : ""}`;
  }

  /** The evidence record (canonical JSON) behind a pack. */
  jsonUrl(kind: EvidenceKind, id: string): string {
    return `/api/evidence/${SEGMENT[kind]}/${encodeURIComponent(id)}.json`;
  }

  /** Builds the pack and saves it as swift-evidence-<name>-<date>.pdf. Resolves to the file name. */
  async download(
    kind: EvidenceKind,
    id: string,
  ): Promise<{
    fileName: string;
    evidenceId: string | null;
    sha256: string | null;
  }> {
    let response;
    try {
      response = await Api.send({
        url: this.pdfUrl(kind, id),
        responseType: "blob",
        timeout: 180000,
        quietError: true,
      });
    } catch (err: any) {
      // The error body of a blob request is a Blob too: read the server's message from it.
      throw new Error(await errorText(err));
    }
    const fileName = fileNameFrom(
      response.headers?.["content-disposition"],
      `swift-evidence-${id}.pdf`,
    );
    saveAs(response.data, fileName);
    return {
      fileName,
      evidenceId: response.headers?.["x-evidence-id"] || null,
      sha256: response.headers?.["x-evidence-sha256"] || null,
    };
  }

  /** The evidence record itself, e.g. to show a summary before downloading. */
  async get(
    kind: EvidenceKind,
    id: string,
  ): Promise<{ evidence: Evidence; sha256: string | null }> {
    const response = await Api.send({
      url: this.jsonUrl(kind, id),
      responseType: "text",
      timeout: 180000,
    });
    const text =
      typeof response.data === "string"
        ? response.data
        : JSON.stringify(response.data);
    return {
      evidence: JSON.parse(text),
      sha256: response.headers?.["x-evidence-sha256"] || null,
    };
  }
}

export default new EvidenceSource();
